/**
 * PublisherIcon — icono del **fabricante** del modelo (Sprint 286 D; diseño §3).
 *
 * Marcas oficiales de fabricantes (OpenAI, Anthropic, Google…) se añaden como
 * assets propios en `assets/publisher-logos/` cuando estén licenciadas; hasta
 * entonces se pinta un **monograma** con el color estable del fabricante
 * (nunca un logo inventado). El icono del **proveedor/vendedor** sigue siendo
 * `ProviderIcon` (logos oficiales ya existentes).
 */

import { publisherSlug } from '../lib/publisher';

interface PublisherIconProps {
  publisher?: string | null;
  size?: number;
  className?: string;
}

const PALETTE: Record<string, string> = {
  openai: '#10a37f',
  anthropic: '#d97757',
  google: '#4285f4',
  meta: '#0866ff',
  mistral: '#fa520f',
  deepseek: '#4d6bfe',
  xai: '#1f2937',
  cohere: '#39594d',
  qwen: '#615ced',
  amazon: '#ff9900',
  microsoft: '#00a4ef',
};

function initials(publisher: string): string {
  const words = publisher
    .split(/[\s/._-]+/)
    .map((word) => word.trim())
    .filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return `${words[0][0]}${words[1][0]}`.toUpperCase();
}

export function PublisherIcon({ publisher, size = 20, className = '' }: PublisherIconProps) {
  const slug = publisherSlug(publisher);
  const label = publisher?.trim() || '—';
  const color = PALETTE[slug] ?? '#6b7280';
  const text = initials(label);
  const fontSize = Math.max(Math.round(size * 0.42), 8);
  return (
    <span
      role="img"
      aria-label={`Publisher: ${label}`}
      title={label}
      data-testid={`publisher-icon-${slug || 'unknown'}`}
      className={`inline-flex shrink-0 items-center justify-center rounded ${className}`}
      style={{
        width: size,
        height: size,
        backgroundColor: `${color}22`,
        color,
        border: `1px solid ${color}55`,
        fontSize,
        fontWeight: 700,
        lineHeight: 1,
      }}
    >
      {text}
    </span>
  );
}
